/* eslint-disable react/prop-types */
import { alpha, Card, CardContent, Checkbox, Grid, Tooltip, Typography } from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view'
import { groupBy, orderBy, uniqBy } from 'lodash'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useTreeItem } from '@mui/x-tree-view/useTreeItem'
import {
    TreeItemContent,
    TreeItemGroupTransition,
    TreeItemIconContainer,
    TreeItemLabel,
    TreeItemRoot,
} from '@mui/x-tree-view/TreeItem'
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon'
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider'
import { useTreeItemModel } from '@mui/x-tree-view/hooks'
import DescriptionIcon from '@mui/icons-material/Description'
import PropTypes from 'prop-types'
import { selectCommercialFeatures } from 'gdl/commercial/commercialFeature/CommercialFeaturesSlice'

const CustomLabel = ({ item, children, ...other }) => (
    <TreeItemLabel {...other}>
        <Grid container spacing={1} alignItems='center'>
            <Grid item>
                <Typography sx={{ fontSize: '1rem' }}>{children}</Typography>
            </Grid>
            {item.description && (
                <Grid item>
                    <Tooltip title={item.description}>
                        <DescriptionIcon />
                    </Tooltip>
                </Grid>
            )}
            <Grid item xs />
            <Grid item>
                <Typography sx={{ fontSize: '1rem' }}>{item.price} €</Typography>
            </Grid>
        </Grid>
    </TreeItemLabel>
)

const CustomTreeItem = React.forwardRef((props, ref) => {
    const {
        id, itemId, label, disabled, children, // from useTreeItem
        getFeatureState,
        onToggleFull,
        onToggleOption,
        ...other
    } = props

    const itemState = getFeatureState(itemId)


    const {
        getContextProviderProps,
        getRootProps,
        getContentProps,
        getIconContainerProps,
        getLabelProps,
        status,
        getGroupTransitionProps,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref })

    const item = useTreeItemModel(itemId)

    return (
        <TreeItemProvider {...getContextProviderProps()}>
            <TreeItemRoot {...getRootProps(other)}>
                <TreeItemContent {...getContentProps()} sx={{ borderRadius: '8px' }}>
                    <TreeItemIconContainer {...getIconContainerProps()}>
                        <TreeItemIcon status={status} slotProps={{ icon: { sx: { fontSize: '48px' } } }} />
                    </TreeItemIconContainer>
                    <CustomLabel {...getLabelProps({ item })} />
                    <Grid item display='flex' alignItems='center' gap={1} ml={1}>
                        <Tooltip title='Livré dans le produit'>
                            <Checkbox
                                size='large'
                                checked={itemState?.mode === 'FULL'}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => onToggleFull(itemId)}
                                sx={{ padding: 0 }}
                            />
                        </Tooltip>
                        <Tooltip title='Livré en Option'>
                            <Checkbox
                                size='small'
                                checked={itemState?.mode === 'OPTION'}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => onToggleOption(itemId)}
                                sx={{ padding: 0 }}
                            />
                        </Tooltip>
                    </Grid>
                </TreeItemContent>
                {children && (
                    <TreeItemGroupTransition
                        {...getGroupTransitionProps()}
                        sx={{
                            marginLeft: '15px',
                            paddingLeft: '8px',
                            borderLeft: `1px dashed ${alpha('#000000', 0.3)}`,
                        }}
                    />
                )}
            </TreeItemRoot>
        </TreeItemProvider>
    )
})

const ProductFeatureSelect2Checkbox = ({
    selectedFeatures = [],
    setSelectedFeatures = () => { },
}) => {
    const commercialFeatures = useSelector(selectCommercialFeatures, shallowEqual)
    const [expandedItems, setExpandedItems] = useState([])

    const getFeatureState = (id) => {
        return selectedFeatures.find(f => f.idFeature === id)
    }

    const updateFeature = (id, treeItems, mode) => {
        const featureMap = new Map(treeItems.map(item => [item.id, item]))
        let currentFeatures = selectedFeatures

        const applyRecursive = (currentId) => {
            const current = featureMap.get(currentId)
            if (!current || !current.children) return

            current.children.forEach(child => {
                const updated = {
                    idFeature: child.id,
                    mode,
                }
                currentFeatures = [updated, ...currentFeatures]
                applyRecursive(child.id)
            })
        }
        applyRecursive(id)

        currentFeatures = [{ idFeature: id, mode }, ...currentFeatures]
        setSelectedFeatures(uniqBy(currentFeatures, 'idFeature').filter(f => f.mode))
    }

    const formattedGroupedCommercialFeatures = useMemo(() => {
        const orderedCommercialFeatures = orderBy(commercialFeatures, ['typeFeature', 'code'], ['desc', 'asc'])
        const groupedCommercialFeatures = groupBy(orderedCommercialFeatures, s => s.parent ?? 'headTree')

        const formatFunction = (filteredCommercialFeatures = [], depth) => {
            return filteredCommercialFeatures.map(({ id, name, description, price }) => {
                const children = formatFunction(groupedCommercialFeatures[id], depth + 1)
                return {
                    id,
                    label: name,
                    price,
                    description,

                    children,
                    depth,
                }
            })
        }

        return formatFunction(groupedCommercialFeatures.headTree, 0)
    }, [commercialFeatures])

    return (
        <Grid container spacing={1} alignItems='flex-start'>
            {formattedGroupedCommercialFeatures.map(commercialFeature => (
                <Grid key={commercialFeature.code} size={6}>
                    <Card>
                        <CardContent>
                            <RichTreeView
                                items={[commercialFeature]}
                                expandedItems={expandedItems}
                                onExpandedItemsChange={(_, ids) => setExpandedItems(ids)}
                                selectedItems={selectedFeatures.map(f => f.idFeature)}
                                // onSelectedItemsChange={(_, ids) => {
                                //     setSelectedIds(ids)
                                // }}
                                slots={{ item: CustomTreeItem }}
                                slotProps={{
                                    item: {
                                        getFeatureState,
                                        onToggleFull: (id) => {
                                            const state = getFeatureState(id)
                                            const newState = {
                                                mode: state?.mode === 'FULL' ? undefined : 'FULL',
                                            }
                                            updateFeature(id, formattedGroupedCommercialFeatures, newState.mode)
                                        },
                                        onToggleOption: (id) => {
                                            const state = getFeatureState(id)
                                            const newState = {
                                                mode: state?.mode === 'OPTION' ? undefined : 'OPTION',
                                            }
                                            updateFeature(id, formattedGroupedCommercialFeatures, newState.mode)
                                        },
                                    },
                                }}
                                itemChildrenIndentation={0}
                                multiSelect
                                checkboxSelection
                            />
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    )
}

ProductFeatureSelect2Checkbox.propTypes = {
    selectedIds: PropTypes.arrayOf(PropTypes.number),
    setSelectedIds: PropTypes.func,
}

export default ProductFeatureSelect2Checkbox